// Child color palette.
// ---------------------
// The app has 6 named Soleil pastel colors. Each child's `color` column stores the
// `value` string (e.g. 'orange').
//
// Preferred: use CSS custom properties via Tailwind's arbitrary-value utilities.
// The matching CSS rules live in src/index.css (.child-<value> blocks).
//
//     <div class="child-orange">
//       <span class="bg-[var(--child-50)] text-[var(--child-900)]">…</span>
//       <span class="bg-[var(--child-300)]/30">…</span>
//       <button class="bg-[var(--child-500)] text-[var(--child-on-contrast)]">…</button>
//     </div>
//
// The outer <ion-page> in TabsPage.vue injects `child-<currentChild.color>`,
// so every descendant picks up the var without an explicit wrapper.
//
// Fallback / hex: when you need a raw hex value (inline SVG stroke, canvas,
// alpha-suffixed hex like `#aabbcc26`, box-shadow ring), read the `hexNNN`
// fields on the info object — they mirror the same shades.

export const CHILD_COLORS = [
  {
    // Corail. C'était « Pêche », une terracotta dont le bout sombre (#9C5B3F)
    // était franchement brun. Même famille chaude, réveillée. La valeur
    // stockée reste `orange` : aucun enfant ne change de colonne.
    name: 'Corail',
    value: 'orange',
    hex50:  '#FFF0EC',
    hex100: '#FFE2DA',
    hex200: '#FFCFC2',
    hex300: '#FBA894',
    hex500: '#F4785F',
    hex900: '#A33D28',
    ink:    '#8E3320',
    onContrast: '#211E16',
  },
  {
    name: 'Miel',
    value: 'yellow',
    hex50:  '#FBF1DC',
    hex100: '#F8E7C4',
    hex200: '#F3D89E',
    hex300: '#EDC676',
    hex500: '#E0A33C',
    hex900: '#8F6418',
    ink:    '#7A5512',
    onContrast: '#211E16',
  },
  {
    name: 'Olive',
    value: 'green',
    hex50:  '#EEF2E6',
    hex100: '#E3E9D8',
    hex200: '#CCD8B8',
    hex300: '#B0C295',
    hex500: '#6E8A57',
    hex900: '#455832',
    ink:    '#3C4D2B',
    onContrast: '#211E16',
  },
  {
    name: 'Ciel doux',
    value: 'blue',
    hex50:  '#EAF1F7',
    hex100: '#DDE8F1',
    hex200: '#C2D6E6',
    hex300: '#9FBDD7',
    hex500: '#6690B3',
    hex900: '#3A5570',
    ink:    '#324A62',
    onContrast: '#211E16',
  },
  {
    name: 'Violet',
    value: 'purple',
    hex50:  '#F6EFFE',
    hex100: '#EFE3FD',
    hex200: '#DFCBF8',
    hex300: '#CBAEF0',
    hex500: '#9C7BC9',
    hex900: '#5E4683',
    ink:    '#513C6E',
    onContrast: '#211E16',
  },
  {
    // Framboise, la sixième. C'est la couleur qui manquait : pêche, miel,
    // olive, ciel, violet, aucun rose. Sa plus courte distance perceptuelle
    // (CIEDE2000) est 20,9 sur le violet et 22,4 sur le corail, quand la paire
    // la plus serrée déjà en production, ciel/violet, est à 17,8.
    // EN DERNIER dans le tableau, et c'est délibéré : `getNextChildColor`
    // distribue dans l'ordre, donc les cinq premiers enfants d'un foyer
    // gardent exactement la couleur qu'ils avaient.
    // ⚠️ `pink` était une valeur LEGACY de `children.color`, jusqu'ici sans
    // palette : ces enfants retombaient sur le pêche. Ils retrouvent un rose.
    name: 'Framboise',
    value: 'pink',
    hex50:  '#FEEBF3',
    hex100: '#FEDDEA',
    hex200: '#FBC6DC',
    hex300: '#F49ABF',
    hex500: '#E8649B',
    hex900: '#A02C5C',
    ink:    '#8E2E5A',
    onContrast: '#211E16',
  },
] as const

export type ChildColor = typeof CHILD_COLORS[number]['value']

export interface ChildColorInfo {
  name: string
  value: ChildColor
  hex50: string
  hex100: string
  hex200: string
  hex300: string
  hex500: string
  hex900: string
  ink: string
  onContrast: string
}

// Default (corail) — used when a child's stored color string is unknown.
// Legacy rows may hold values no longer in CHILD_COLORS (red/indigo/…).
// `pink` en faisait partie ; il est redevenu une vraie couleur (framboise).
export const DEFAULT_CHILD_COLOR: ChildColorInfo = CHILD_COLORS[0]

export function getChildColorInfo(colorValue: string | null | undefined): ChildColorInfo {
  const found = CHILD_COLORS.find(c => c.value === colorValue)
  return (found ?? DEFAULT_CHILD_COLOR) as ChildColorInfo
}

// The CSS class name that activates a given child's palette on a subtree.
// Use when you need to scope a kid's color to a specific element (card in a
// list of kids, modal, etc.) rather than relying on the page-level wrapper.
//
//     <div :class="childColorClass(child.color)">
//       <span class="bg-[var(--child-50)]">…</span>
//     </div>
export function childColorClass(colorValue: string | null | undefined): string {
  return `child-${getChildColorInfo(colorValue).value}`
}

// Deterministic next-color picker for auto-assigning colors to new children.
export function getNextChildColor(childIndex: number): ChildColor {
  return CHILD_COLORS[childIndex % CHILD_COLORS.length].value
}

export function getAvailableChildColors(): ChildColor[] {
  return CHILD_COLORS.map(c => c.value)
}
